/**
 * DeviceCallbackPage — result screen after Keycloak OAuth completes
 *
 * Mounted at /device/callback. The API's handleDeviceCallback redirects here
 * with ?success=true or ?error=<message> after processing the auth code.
 *
 * @param success - "true" if authorized
 * @param error   - human-readable error message on failure
 */

import { CheckCircle2, XCircle } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/Card';
import { TrafficLightLogo } from '@/components/TrafficLightLogo';
import { useDocumentTitle } from '@/hooks/useDocumentTitle';

export default function DeviceCallbackPage() {
  useDocumentTitle('Device Authorization');
  const params = new URLSearchParams(window.location.search);
  const success = params.get('success') === 'true';
  const error = params.get('error') ?? 'Authorization failed. Please try again from your terminal.';

  return (
    <div className="min-h-screen flex items-center justify-center bg-[rgb(5,5,5)] p-4">
      <Card className="w-full max-w-md bg-zinc-800/60 border-zinc-700 backdrop-blur text-center">
        <CardHeader className="space-y-4">
          <div className="flex justify-center"><TrafficLightLogo size="md" /></div>
          {success ? (
            <>
              <CheckCircle2 className="h-14 w-14 text-green-400 mx-auto" />
              <CardTitle className="text-white text-2xl">Authorization Complete</CardTitle>
              <CardDescription className="text-zinc-400">
                You can close this window and return to your terminal or IDE.
              </CardDescription>
            </>
          ) : (
            <>
              <XCircle className="h-14 w-14 text-red-400 mx-auto" />
              <CardTitle className="text-white text-2xl">Authorization Failed</CardTitle>
              <CardDescription className="text-zinc-400">
                Something went wrong. Please try again from your terminal or IDE.
              </CardDescription>
            </>
          )}
        </CardHeader>

        {!success && (
          <CardContent>
            <p className="rounded-lg border border-red-500/30 bg-red-500/10 px-4 py-3 text-sm text-red-400">
              {error}
            </p>
          </CardContent>
        )}
      </Card>
    </div>
  );
}
